import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import i18n from "./index";

export type Language = "en" | "zh" | "ja" | "th";

const STORAGE_KEY = "xecguard.language";
const SUPPORTED: ReadonlyArray<Language> = ["en", "zh", "ja", "th"];

// localStorage can throw (private mode, storage disabled by browser policy,
// third-party-cookie restrictions in iframes, etc). Since LanguageProvider
// wraps the whole app, any uncaught throw at boot would break every route —
// including /login. Swallow failures and fall back to in-memory "en".
const readStoredLanguage = (): Language => {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored as Language) ? (stored as Language) : "en";
  } catch {
    return "en";
  }
};

const writeStoredLanguage = (lang: Language): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Storage unavailable: keep the language in React state only.
  }
};

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
};

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
});

const syncDocumentLang = (lang: Language): void => {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
};

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  useEffect(() => {
    writeStoredLanguage(language);
    syncDocumentLang(language);
    if (i18n.language !== language) {
      void i18n.changeLanguage(language);
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => useContext(LanguageContext);
